import React from 'react';
import { Box, Label, Radio, spacing } from '@apia/theme';
import { shallowEqual } from 'react-redux';
import MakeField from '../fieldsStore/MakeField';
import { IApiaRadioButton, TApiaRadioPossibleValue } from '../../../../types';
import useContext from '../../../../hooks/useContext';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import FieldContext from '../fieldsStore/FieldContext';
import IProperty from '../../../../JSApi/constants/IProperty';
import { arrayOrArray } from '../../../../utils/util';
import { getFieldErrorStyles } from '../style';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import { getSelectedValues } from './ApiaMultiple';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import propsStore from '../../../../store/propsStore/propsStore';
import { useApiaFormContext } from '../page/ApiaForm';
import { RequiredLbl } from '../common';

const getPossibleValues = (
  field: IApiaRadioButton,
): TApiaRadioPossibleValue[] | [] => {
  return arrayOrArray(field.possibleValue);
};

const ApiaRadioButtonRenderer = React.forwardRef<HTMLInputElement>((_, ref) => {
  const {
    state: { value, isValid },
    setValue,
    fireEvent,
  } = useContext(FieldContext);
  const { fieldId } = useContext(ApiaComponentContextPrimitive);
  const field = usePropsSelector(
    fieldId,
    (current) => current.field as IApiaRadioButton,
    shallowEqual,
  );
  const {
    attId,
    attLabel,
    valueType,
    properties: { dontBreakRadio: shouldShowInline },
  } = field;
  const props = usePropsSelector(
    fieldId,
    (cp) => ({
      [IProperty.PROPERTY_REQUIRED]: cp[IProperty.PROPERTY_REQUIRED],
      [IProperty.PROPERTY_VISIBILITY_HIDDEN]:
        cp[IProperty.PROPERTY_VISIBILITY_HIDDEN],
      attributes: cp.attributes,
      [IProperty.PROPERTY_VALUE_COLOR]: cp[IProperty.PROPERTY_VALUE_COLOR],
      [IProperty.PROPERTY_INPUT_AS_TEXT]: cp[IProperty.PROPERTY_INPUT_AS_TEXT],
      [IProperty.PROPERTY_TOOLTIP]: cp[IProperty.PROPERTY_TOOLTIP],
      [IProperty.PROPERTY_DISABLED]: cp[IProperty.PROPERTY_DISABLED],
      [IProperty.PROPERTY_AUTO_FOCUS]: cp[IProperty.PROPERTY_AUTO_FOCUS],
      PROPERTY_ARIA_LABEL: cp[IProperty.PROPERTY_ARIA_LABEL],
      [IProperty.PROPERTY_TITLE]: cp[IProperty.PROPERTY_TITLE],
      [IProperty.PROPERTY_ARIA_DESCRIPTION]:
        cp[IProperty.PROPERTY_ARIA_DESCRIPTION],
      possibleValue: cp.possibleValue
        ? arrayOrArray(cp.possibleValue)
        : undefined,
    }),
    shallowEqual,
  );
  const { identifier, isReadonly } = useApiaFieldCommonProps({
    className: 'radio__field',
  });

  const { isFormReadOnly } = useApiaFormContext();
  return (
    <Box
      {...window.getVariant('layout.execution.form.fields.radio')}
      as="fieldset"
      aria-label={props[IProperty.PROPERTY_AUTO_FOCUS]}
      aria-description={props[IProperty.PROPERTY_ARIA_DESCRIPTION]}
      title={props[IProperty.PROPERTY_TITLE]}
      key={`radio_${fieldId}`}
      data-required={props[IProperty.PROPERTY_REQUIRED]}
      sx={React.useMemo(
        () => ({
          columnGap: props.attributes?.onGrid ? 4 : null,
          p: props.attributes?.onGrid ? 2 : null,
          ...getFieldErrorStyles(isValid),
          display: 'flex',
          flexDirection: String(shouldShowInline) === 'true' ? 'row' : 'column',
          flexWrap: 'wrap',
          listStyle: 'none',
          border: 'none',
          pl: spacing(0),
          ...(isReadonly
            ? {
                '& svg': {
                  color: '#777 ',
                  bg: '#f5f5f5',
                },
              }
            : {}),
        }),
        [isReadonly, isValid, props.attributes?.onGrid, shouldShowInline],
      )}
      {...identifier}
      className={`${identifier.className} ${shouldShowInline ? 'inline' : ''}`}
    >
      <div className="radio__layout__wrapper">
        {!props.attributes.onGrid && (
          <legend className="radio_label">
            {window.SHOW_REQUIRED_POSITION !== '0' && (
              <RequiredLbl
                required={props[IProperty.PROPERTY_REQUIRED]}
                isFormReadonly={isFormReadOnly}
              />
            )}
            {attLabel}:
            {window.SHOW_REQUIRED_POSITION === '0' && (
              <RequiredLbl
                required={props[IProperty.PROPERTY_REQUIRED]}
                isFormReadonly={isFormReadOnly}
              />
            )}
          </legend>
        )}
        <ul className="options_wrapper">
          {!(isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) &&
            (props.possibleValue ?? getPossibleValues(field)).map(
              (radio: TApiaRadioPossibleValue, idx: number) => {
                let rValue;
                if (valueType === 'S') {
                  rValue = radio.value.toString();
                } else {
                  rValue = radio.value;
                }
                return (
                  <Box
                    as="li"
                    className="option_wrapper"
                    sx={{ display: 'flex', gap: 3 }}
                    key={`lbl_${fieldId}_${radio.label}_${attId}_${idx}`}
                  >
                    <Label
                      htmlFor={`${fieldId}_${idx}`}
                      title={radio.label}
                      sx={{
                        mb: '1',
                        color: props[IProperty.PROPERTY_VALUE_COLOR],
                      }}
                      className="executionForm__radio"
                    >
                      <Radio
                        autoFocus={
                          idx === 0 &&
                          props[IProperty.PROPERTY_AUTO_FOCUS] === 'true'
                            ? true
                            : undefined
                        }
                        key={`${fieldId}_${rValue}_${radio.label}_${idx}`}
                        data-type="radio"
                        id={`${fieldId}_${idx}`}
                        name={fieldId}
                        data-name={fieldId}
                        ref={
                          (!value && idx === 0) || value === rValue
                            ? ref
                            : undefined
                        }
                        data-field-id={fieldId}
                        data-att-id={fieldId}
                        data-radio-index={idx}
                        title={
                          props[IProperty.PROPERTY_TITLE] ||
                          props[IProperty.PROPERTY_TOOLTIP] ||
                          attLabel
                        }
                        required={props[IProperty.PROPERTY_REQUIRED]}
                        disabled={props[IProperty.PROPERTY_DISABLED]}
                        data-checked={value === rValue}
                        checked={String(value) === String(rValue)}
                        value={rValue}
                        onChange={(e) => {
                          setValue(e.target.value);
                          fireEvent('onChange');
                        }}
                      />
                      {!props[IProperty.PROPERTY_DISABLED] && (
                        <Box
                          as="span"
                          sx={{
                            fontWeight: 'body',
                          }}
                        >
                          {radio.label}
                        </Box>
                      )}
                      {props[IProperty.PROPERTY_DISABLED] && (
                        <Box
                          as="span"
                          id={`lbl_${rValue}`}
                          sx={{
                            color: 'muted',
                            fontWeight: 'body',
                          }}
                        >
                          {radio.label}
                        </Box>
                      )}
                    </Label>
                  </Box>
                );
              },
            )}
        </ul>
        {(isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) &&
          (props.possibleValue ?? getPossibleValues(field))?.map((radio, i) => {
            if (value === radio.value.toString()) {
              return (
                <Box
                  id={`${fieldId}_${i}`}
                  title={radio?.label}
                  data-radio-index={i}
                  key={`option_${fieldId}_${radio?.value}_${radio?.label}`}
                >
                  {radio?.label}
                </Box>
              );
            }
            return null;
          })}
      </div>
    </Box>
  );
});

const ApiaRadioButton = MakeField(ApiaRadioButtonRenderer, {
  getInitialValue({ context: { fieldId } }) {
    const { possibleValue } = propsStore.getFieldProps(fieldId)
      .field as IApiaRadioButton;
    const value = getSelectedValues(possibleValue ?? []);
    if (!value || (Array.isArray(value) && value.length === 0)) return '';
    return value[0];
  },
  doScriptEvents: async ({ fireScriptEvents }) => {
    let hasFiredScriptEventsCorrectly = await fireScriptEvents('onClick');
    if (!hasFiredScriptEventsCorrectly) return false;

    hasFiredScriptEventsCorrectly = await fireScriptEvents('onChange');
    return hasFiredScriptEventsCorrectly;
  },
  doServerEvents: ({ fieldContext, fireServerEvents }) => {
    const events = fieldContext.componentContext.field.serverEvents;
    if (events) {
      if (events.find((searchEvent) => searchEvent.evtName === 'onChange'))
        return fireServerEvents('onChange');
      return fireServerEvents('onClick');
    }
    return true;
  },
});

export default ApiaRadioButton;
